import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { playClick, playSuccess } from '@/utils/sounds';
import {
  RetroBackground,
  RetroPanel,
  RetroButton,
  RetroPageTitle,
  bandedGradient,
  PALETTE,
  ACCENTS,
  DESIGN_TOKENS
} from '@/components/RetroUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const tileStyle = (isSelected) => ({
  border: `3px solid ${PALETTE.ink}`,
  borderRadius: DESIGN_TOKENS.borderRadius.md,
  background: isSelected
    ? bandedGradient(ACCENTS.goldLight, ACCENTS.gold, ACCENTS.goldDark)
    : bandedGradient('#FFFFFF', '#FFFFFF', PALETTE.cream),
  color: PALETTE.ink,
  boxShadow: isSelected
    ? `inset 1px 1px 0px #FFF3C4, 2px 2px 0px ${PALETTE.ink}`
    : `2px 2px 0px ${PALETTE.ink}`,
});

export default function OnlineQueue() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [playlists, setPlaylists] = useState([]);
  const [teamSize, setTeamSize] = useState(1);
  const [innings, setInnings] = useState(3);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [phase, setPhase] = useState('loading'); // loading | setup | searching
  const [elapsed, setElapsed] = useState(0);
  const [queuePosition, setQueuePosition] = useState(null);
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  // Load playlists + resume any existing queue/match
  useEffect(() => {
    const init = async () => {
      try {
        const [plRes, matchRes, queueRes] = await Promise.all([
          fetch(`${API_URL}/api/apple-music/playlists`),
          fetch(`${API_URL}/api/online/match`, { headers: getAuthHeader() }),
          fetch(`${API_URL}/api/online/queue/status`, { headers: getAuthHeader() }),
        ]);
        const pls = await plRes.json();
        setPlaylists(pls);
        if (pls.length > 0) setSelectedPlaylist(pls[0].id);

        const matchData = await matchRes.json();
        if (matchData.match_id && matchData.state !== 'completed') {
          navigate(`/online/lobby/${matchData.match_id}`);
          return;
        }

        const queueData = await queueRes.json();
        if (queueData.matched && queueData.match_id) {
          navigate(`/online/lobby/${queueData.match_id}`);
          return;
        }
        if (queueData.in_queue) {
          setQueuePosition(queueData.queue_position);
          setPhase('searching');
          return;
        }
        setPhase('setup');
      } catch (e) {
        toast.error('Failed to load online play');
        setPhase('setup');
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll queue status while searching
  const pollStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/online/queue/status`, { headers: getAuthHeader() });
      const data = await res.json();
      if (data.matched && data.match_id) {
        playSuccess();
        navigate(`/online/lobby/${data.match_id}`);
        return;
      }
      if (data.in_queue) {
        setQueuePosition(data.queue_position);
      }
    } catch (e) {
      // Keep polling
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase === 'searching') {
      pollRef.current = setInterval(pollStatus, 2000);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    }
    return () => {
      clearInterval(pollRef.current);
      clearInterval(timerRef.current);
    };
  }, [phase, pollStatus]);

  const handleFindMatch = async () => {
    playClick();
    if (!selectedPlaylist) {
      toast.error('Select a playlist first');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/online/queue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ team_size: teamSize, innings, playlist_id: selectedPlaylist }),
      });
      if (!res.ok) throw new Error('queue failed');
      setElapsed(0);
      setPhase('searching');
      // Immediate check - we may have completed a match instantly
      setTimeout(pollStatus, 400);
    } catch (e) {
      toast.error('Failed to join queue');
    }
  };

  const handleCancel = async () => {
    playClick();
    try {
      await fetch(`${API_URL}/api/online/queue`, {
        method: 'DELETE',
        headers: getAuthHeader(),
      });
    } catch (e) {
      // ignore
    }
    setPhase('setup');
    setElapsed(0);
  };

  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  if (phase === 'loading') {
    return (
      <RetroBackground variant="brownStripes" className="p-4 flex items-center justify-center">
        <p style={{ fontFamily: DESIGN_TOKENS.fonts.ui, fontSize: '14px', color: PALETTE.cream }}>
          Loading...
        </p>
      </RetroBackground>
    );
  }

  if (phase === 'searching') {
    return (
      <RetroBackground variant="brownStripes" className="p-4 md:p-8">
        <div className="max-w-md mx-auto pt-12">
          <RetroPageTitle color="cream" size="md" className="mb-8">
            FINDING MATCH
          </RetroPageTitle>

          {/* LED search panel */}
          <div
            className="text-center p-6 mb-6"
            style={{
              background: PALETTE.scoreboardBg,
              border: `4px solid ${PALETTE.ink}`,
              borderRadius: DESIGN_TOKENS.borderRadius.md,
              boxShadow: `5px 5px 0px rgba(0,0,0,0.4)`,
            }}
            data-testid="queue-searching-panel"
          >
            <p
              className="animate-pulse"
              style={{
                fontFamily: DESIGN_TOKENS.fonts.display,
                fontSize: '12px',
                color: PALETTE.scoreboardText,
                textShadow: `0 0 10px ${PALETTE.scoreboardText}`,
                letterSpacing: '2px',
                marginBottom: '16px',
              }}
            >
              SEARCHING FOR PLAYERS...
            </p>
            <p
              style={{
                fontFamily: DESIGN_TOKENS.fonts.display,
                fontSize: '32px',
                color: PALETTE.scoreboardText,
                textShadow: `0 0 12px ${PALETTE.scoreboardText}88`,
                marginBottom: '12px',
              }}
              data-testid="queue-elapsed-time"
            >
              {formatTime(elapsed)}
            </p>
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontSize: '13px',
              color: `${PALETTE.cream}99`,
            }}>
              {teamSize}v{teamSize} • {innings} innings
              {queuePosition ? ` • #${queuePosition} in line` : ''}
            </p>
          </div>

          <RetroButton
            variant="danger"
            size="md"
            fullWidth
            onClick={handleCancel}
            data-testid="cancel-queue-btn"
          >
            CANCEL SEARCH
          </RetroButton>
        </div>
      </RetroBackground>
    );
  }

  return (
    <RetroBackground variant="brownStripes" className="p-4 md:p-8">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <RetroButton
            variant="secondary"
            size="sm"
            onClick={() => { playClick(); navigate('/dashboard'); }}
            data-testid="back-btn"
          >
            <ChevronLeft size={20} />
          </RetroButton>
          <div className="flex-1">
            <RetroPageTitle color="cream" size="md">
              PLAY ONLINE
            </RetroPageTitle>
          </div>
          <div style={{ width: '52px' }} />
        </div>

        <RetroPanel title="MATCH SETUP" variant="light">
          {/* Team size */}
          <div className="mb-5">
            <h3
              className="text-center"
              style={{
                fontFamily: DESIGN_TOKENS.fonts.display,
                fontSize: DESIGN_TOKENS.fontSizes.displaySm,
                color: PALETTE.grass,
                marginBottom: '12px',
                letterSpacing: '1px',
              }}
            >
              TEAM SIZE
            </h3>
            <div className="flex gap-2 justify-center">
              {[1, 2, 3, 4, 5].map((size) => (
                <button
                  key={size}
                  onClick={() => { playClick(); setTeamSize(size); }}
                  className="transition-all"
                  style={{
                    ...tileStyle(teamSize === size),
                    width: '56px',
                    height: '48px',
                    fontFamily: DESIGN_TOKENS.fonts.ui,
                    fontWeight: 700,
                    fontSize: '14px',
                  }}
                  data-testid={`team-size-${size}`}
                >
                  {size}v{size}
                </button>
              ))}
            </div>
          </div>

          {/* Innings */}
          <div className="mb-5">
            <h3
              className="text-center"
              style={{
                fontFamily: DESIGN_TOKENS.fonts.display,
                fontSize: DESIGN_TOKENS.fontSizes.displaySm,
                color: PALETTE.grass,
                marginBottom: '12px',
                letterSpacing: '1px',
              }}
            >
              INNINGS
            </h3>
            <div className="flex gap-2 justify-center">
              {[3, 5, 9].map((n) => (
                <button
                  key={n}
                  onClick={() => { playClick(); setInnings(n); }}
                  className="transition-all"
                  style={{
                    ...tileStyle(innings === n),
                    width: '56px',
                    height: '52px',
                    fontFamily: DESIGN_TOKENS.fonts.display,
                    fontSize: '16px',
                  }}
                  data-testid={`online-innings-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Playlist preference */}
          <div className="mb-6">
            <h3
              className="text-center"
              style={{
                fontFamily: DESIGN_TOKENS.fonts.display,
                fontSize: DESIGN_TOKENS.fontSizes.displaySm,
                color: PALETTE.grass,
                marginBottom: '4px',
                letterSpacing: '1px',
              }}
            >
              PREFERRED PLAYLIST
            </h3>
            <p className="text-center" style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontSize: '11px',
              color: `${PALETTE.ink}99`,
              marginBottom: '12px',
            }}>
              One player&apos;s pick is chosen at random
            </p>
            <div className="space-y-2">
              {playlists.map((pl) => {
                const isSelected = selectedPlaylist === pl.id;
                return (
                  <button
                    key={pl.id}
                    onClick={() => { playClick(); setSelectedPlaylist(pl.id); }}
                    className="w-full px-4 py-2 text-center transition-all"
                    style={{
                      ...tileStyle(isSelected),
                      borderRadius: DESIGN_TOKENS.borderRadius.pill,
                    }}
                    data-testid={`online-playlist-${pl.id}`}
                  >
                    <span style={{
                      fontFamily: DESIGN_TOKENS.fonts.ui,
                      fontWeight: isSelected ? 700 : 600,
                      fontSize: '14px',
                      color: PALETTE.ink,
                    }}>
                      {pl.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <RetroButton
            variant="primary"
            size="md"
            fullWidth
            onClick={handleFindMatch}
            data-testid="find-match-btn"
          >
            FIND MATCH
          </RetroButton>
        </RetroPanel>
      </div>
    </RetroBackground>
  );
}
